import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { shizaiApi } from './shizaiApi';
import { DataTable } from '@/components/ui/DataTable';

export default function ReorderSuggestions() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);
  const [departments, setDepartments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [qtyMap, setQtyMap] = useState({});
  // Per-row supplier override (Feb 2026): buyer can pick a different supplier
  // than the item master's default straight from this screen. The pick is
  // persisted onto the created REQ so downstream Kanban/PO flows use it.
  const [supplierMap, setSupplierMap] = useState({});
  const [priority, setPriority] = useState('normal');
  const [neededBy, setNeededBy] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = deptFilter ? { department_id: deptFilter } : {};
    shizaiApi.reorderSuggestions(params).then((r) => {
      const s = r.suggestions || [];
      setList(s);
      const qm = {};
      const sm = {};
      // For pack items: default input is in packs. For others: in pieces (uom).
      s.forEach((x) => {
        qm[x.item_id] = x.pack_size > 0 ? (x.pack_count || 1) : x.suggest_qty;
        sm[x.item_id] = x.supplier_id || '';
      });
      setQtyMap(qm);
      setSupplierMap(sm);
      setSelected(new Set());
    }).catch(() => {});
  }, [deptFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { shizaiApi.departments().then(setDepartments).catch(() => {}); }, []);
  useEffect(() => { shizaiApi.suppliers().then(setSuppliers).catch(() => {}); }, []);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = list.length > 0 && selected.size === list.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(list.map((x) => x.item_id)));
  const setQty = (id, v) => setQtyMap((p) => ({ ...p, [id]: v }));
  const setSupplier = (id, v) => setSupplierMap((p) => ({ ...p, [id]: v }));

  const create = async () => {
    const lines = list.filter((x) => selected.has(x.item_id))
      .map((x) => {
        const entered = Number(qtyMap[x.item_id]) || 0;
        // Convert 袋 → 個 when the item has a pack size (Shizai types packs, we store pieces)
        const qty = x.pack_size > 0 ? entered * x.pack_size : entered;
        return {
          item_id: x.item_id,
          qty,
          supplier_id: supplierMap[x.item_id] || null,
        };
      })
      .filter((l) => l.qty > 0);
    if (lines.length === 0) { toast.error(t('shizai.reorder.noneSelected')); return; }
    setBusy(true);
    try {
      await shizaiApi.createReorderRequests({ lines, department_id: deptFilter || null, priority, needed_by: neededBy || null });
      toast.success(t('shizai.reorder.created'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-reorder">
      <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('shizai.reorder.title')}</h1>

      {/* department filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="reorder-dept-filter">
        <button onClick={() => setDeptFilter('')} data-testid="reorder-dept-all"
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!deptFilter ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
          {t('mfg.dept.all')}
        </button>
        {departments.map((d) => (
          <button key={d.id} onClick={() => setDeptFilter(d.id)} data-testid={`reorder-dept-${d.code}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${deptFilter === d.id ? 'text-white border-transparent' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
            style={deptFilter === d.id ? { backgroundColor: d.color } : undefined}>
            <span className="w-2 h-2 rounded-full" style={{ background: deptFilter === d.id ? '#fff' : d.color }} />
            {d.name}
          </button>
        ))}
      </div>

      {canWrite && list.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap bg-white border border-zinc-200 rounded-xl p-3 shadow-sm">
          <Button variant="outline" size="sm" className="h-9" data-testid="reorder-select-all" onClick={toggleAll}>
            {allSelected ? t('shizai.reorder.clearAll') : t('shizai.reorder.selectAll')}
          </Button>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-zinc-500">{t('shizai.reorder.priority')}</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="reorder-priority" className="h-9 w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{['low', 'normal', 'high'].map((p) => <SelectItem key={p} value={p}>{t(`shizai.priority.${p}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-zinc-500">{t('shizai.reorder.neededBy')}</Label>
            <div className="w-40"><DateField value={neededBy} onChange={(v) => setNeededBy(v)} inputClassName="h-9" testId="reorder-needed-by" /></div>
          </div>
          <div className="flex-1" />
          <Button data-testid="reorder-create-btn" disabled={busy || selected.size === 0} onClick={create}
            className="h-9 bg-teal-700 hover:bg-teal-800 shadow-sm">
            {t('shizai.reorder.create')} ({selected.size})
          </Button>
        </div>
      )}

      <DataTable
        dataTestId="reorder-table"
        rows={list}
        rowKey={(r) => r.item_id}
        rowTestId={(r) => `reorder-row-${r.item_id}`}
        empty={{ icon: <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('shizai.reorder.empty'), testid: 'reorder-empty' }}
        columns={[
          { key: 'sel', header: '', width: 'w-10', sortable: false, cell: (r) => (
            <input type="checkbox" data-testid={`reorder-select-${r.item_id}`} checked={selected.has(r.item_id)}
              onChange={() => toggle(r.item_id)} className="w-4 h-4 accent-rose-500 cursor-pointer" />
          ) },
          { key: 'name', primary: true, header: t('shizai.reorder.item'), flex: true, cell: (r) => (
            <span className="block min-w-0 text-zinc-800"><span className="line-clamp-2 break-words" title={r.name}>{r.name}</span><span className="block truncate text-[11px] text-zinc-400 font-mono">{r.sku}</span></span>
          ) },
          { key: 'on_hand', header: t('shizai.reorder.onHand'), align: 'right', sortValue: (r) => r.on_hand, cell: (r) => {
            const hasPack = r.pack_size && r.pack_size > 0;
            return (
              <span className="font-semibold tabular-nums text-rose-600">
                {hasPack ? `${(r.on_hand / r.pack_size).toFixed(1)} ${r.pack_uom || '袋'}` : `${r.on_hand} ${r.uom}`}
                {hasPack && <span className="block text-[10px] text-zinc-400 font-normal">= {r.on_hand} {r.uom}</span>}
              </span>
            );
          } },
          { key: 'reorder_point', header: t('shizai.reorder.reorderPoint'), align: 'right', sortValue: (r) => r.reorder_point, cell: (r) => {
            const hasPack = r.pack_size && r.pack_size > 0;
            return (
              <span className="tabular-nums text-zinc-600">
                {hasPack ? `${Math.ceil(r.reorder_point / r.pack_size)} ${r.pack_uom || '袋'}` : r.reorder_point}
                {hasPack && <span className="block text-[10px] text-zinc-400">= {r.reorder_point} {r.uom}</span>}
              </span>
            );
          } },
          { key: 'suggest', header: t('shizai.reorder.suggestQty'), sortable: false, cell: (r) => {
            const hasPack = r.pack_size && r.pack_size > 0;
            const curInput = Number(qtyMap[r.item_id]) || 0;
            const totalPieces = hasPack ? curInput * r.pack_size : curInput;
            return (
              <div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" step="1" data-testid={`reorder-qty-${r.item_id}`} value={qtyMap[r.item_id] ?? ''}
                    onChange={(e) => setQty(r.item_id, e.target.value)} className="h-9 w-20" />
                  <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">
                    {hasPack ? (r.pack_uom || '袋') : r.uom}
                  </span>
                </div>
                {hasPack && (
                  <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`reorder-pack-${r.item_id}`}>
                    = {totalPieces} {r.uom} (1{r.pack_uom || '袋'}={r.pack_size}{r.uom})
                  </div>
                )}
              </div>
            );
          } },
          { key: 'supplier_name', header: t('shizai.reorder.supplier'), cell: (r) => (
            canWrite ? (
              <select
                value={supplierMap[r.item_id] ?? ''}
                onChange={(e) => setSupplier(r.item_id, e.target.value)}
                data-testid={`reorder-supplier-select-${r.item_id}`}
                className={`h-9 w-full max-w-[220px] rounded-md border px-2 text-xs font-medium outline-none focus:ring-2 focus:ring-rose-200 ${
                  supplierMap[r.item_id]
                    ? 'border-zinc-300 bg-white text-zinc-800'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
                title={supplierMap[r.item_id] ? '仕入先を変更します' : '仕入先未指定 — 選択してください'}
              >
                <option value="">未指定</option>
                {(suppliers || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
                ))}
              </select>
            ) : (
              <span className="text-zinc-600 text-sm">{r.supplier_name || '—'}</span>
            )
          ) },
          { key: 'pending', header: '', sortable: false, cell: (r) => (
            r.pending_requests > 0
              ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid={`reorder-pending-${r.item_id}`}>{t('shizai.reorder.pending')} {r.pending_requests}</Badge>
              : null
          ) },
        ]}
      />
    </div>
  );
}
